import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../../../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import {
  Box, Typography, Button, CircularProgress, IconButton,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import BoltIcon from '@mui/icons-material/Bolt';
import { useQRLogo, useCompanyLogo } from '../../../hooks/useQRLogo';
import PrintCoverPage from '../../../components/PrintCoverPage';

const accent = '#E05500';

const QUADS = [
  {
    key:    'strength',
    letter: 'S',
    label:  'Styrkor',
    color:  '#4CAF50',
    dark:   '#1B5E20',
    Icon:   EmojiEventsIcon,
    prompt: 'Vad är vi bra på? Vilka fördelar har vi gentemot andra?',
    hint:   'Interna fördelar ni råder över. Kompetenser, resurser, relationer, processer.',
    examples: ['Lång branscherfarenhet', 'Starka kundrelationer', 'Snabbt beslutsfattande'],
  },
  {
    key:    'weakness',
    letter: 'W',
    label:  'Svagheter',
    color:  '#EF5350',
    dark:   '#B71C1C',
    Icon:   WarningAmberIcon,
    prompt: 'Vad behöver vi förbättra? Var halkar vi efter?',
    hint:   'Interna brister ni kan påverka. Var tappar ni tid, energi eller affärer?',
    examples: ['Begränsad digital kapacitet', 'Beroende av nyckelpersoner', 'Långa ledtider'],
  },
  {
    key:    'opportunity',
    letter: 'O',
    label:  'Möjligheter',
    color:  '#42A5F5',
    dark:   '#0D47A1',
    Icon:   TipsAndUpdatesIcon,
    prompt: 'Vad kan vi dra nytta av? Vilka dörrar öppnar sig?',
    hint:   'Externa faktorer i omvärlden ni kan utnyttja. Trender, nya krav, teknikutveckling.',
    examples: ['Ökad efterfrågan', 'Nya upphandlingar', 'AI-verktyg effektiviserar'],
  },
  {
    key:    'threat',
    letter: 'T',
    label:  'Hot',
    color:  '#E05500',
    dark:   '#7F2700',
    Icon:   BoltIcon,
    prompt: 'Vad oroar oss? Vad kan ställa till det om vi inte hanterar det?',
    hint:   'Externa faktorer som kan skada er. Konkurrenter, regelförändringar, kompetensbrist.',
    examples: ['Ökad konkurrens', 'Kompetensbrist', 'Stigande kostnader'],
  },
];

export default function SwotNPrint() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState('a4');
  const [showBanner, setShowBanner] = useState(true);
  const companyLogo = useCompanyLogo();
  const qrLogo = useQRLogo();

  useEffect(() => {
    getDoc(doc(db, 'swot_sessions', id)).then(snap => {
      if (snap.exists()) setSession({ id: snap.id, ...snap.data() });
      setLoading(false);
    });
  }, [id]);

  if (loading) return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <CircularProgress sx={{ color: accent }} />
    </Box>
  );
  if (!session) return <Box sx={{ p: 4 }}><Typography>SWOT-sessionen hittades inte.</Typography></Box>;

  const origin  = window.location.origin;
  const isA4    = pageSize === 'a4';
  const groups  = session.groups?.length > 0 ? session.groups : null; // null = inga grupper

  // ── Skalningsconfig per pappersstorlek ────────────────────────────────────────
  const c = {
    maxWidth:         isA4 ? 750  : 1060,
    minHeight:        isA4 ? 440  : 620,
    letterSize:       isA4 ? '4rem'  : '5.5rem',
    labelSize:        isA4 ? '0.85rem' : '1.1rem',
    titleSize:        isA4 ? '0.6rem'  : '0.78rem',
    promptSize:       isA4 ? '0.92rem' : '1.2rem',
    hintSize:         isA4 ? '0.62rem' : '0.78rem',
    tipsFontSize:     isA4 ? '0.46rem' : '0.54rem',
    footerLabelSize:  isA4 ? '0.46rem' : '0.54rem',
    footerTextSize:   isA4 ? '0.48rem' : '0.56rem',
    footerUrlSize:    isA4 ? '0.42rem' : '0.5rem',
    nameLabelSize:    isA4 ? '0.46rem' : '0.54rem',
    nameLineHeight:   isA4 ? 13   : 16,
    qrSize:           isA4 ? 64   : 84,
    qrLogoSize:       isA4 ? 15   : 20,
    companyLogoH:     isA4 ? 32   : 44,
    exampleFontSize:  isA4 ? '0.5rem' : '0.6rem',
  };

  const scanUrl = (quadrant, groupId) =>
    `${origin}/swot-scan?s=${id}&q=${quadrant}${groupId ? `&g=${groupId}` : ''}`;

  return (
    <>
      {/* Screen toolbar */}
      <Box className="no-print" sx={{
        display: 'flex', alignItems: 'center', gap: 2, p: 2,
        borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper',
      }}>
        <IconButton onClick={() => navigate(`/swotn/${id}`)} sx={{ color: 'text.secondary' }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography fontWeight={700} sx={{ flex: 1 }}>
          Skriv ut hybridblad — {session.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
          {groups
            ? `4 kvadranter × ${groups.length} grupper = ${4 * groups.length} blad`
            : '4 blad — ett per SWOT-kvadrant'}
        </Typography>
        <ToggleButtonGroup value={pageSize} exclusive size="small"
          onChange={(_, v) => { if (v) setPageSize(v); }} sx={{ mr: 1 }}>
          <ToggleButton value="a4" sx={{ textTransform: 'none', px: 2, fontSize: '0.78rem' }}>A4 liggande</ToggleButton>
          <ToggleButton value="a3" sx={{ textTransform: 'none', px: 2, fontSize: '0.78rem' }}>A3 liggande</ToggleButton>
        </ToggleButtonGroup>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}
          sx={{ bgcolor: accent, '&:hover': { bgcolor: '#C94D00' }, textTransform: 'none', fontWeight: 600 }}>
          Skriv ut
        </Button>
      </Box>

      {/* Instruktioner på skärmen */}
      {showBanner && (
        <Box className="no-print" sx={{
          bgcolor: '#fff8f4', border: '1px solid #ffd5b8',
          borderRadius: 2, mx: 3, mt: 2, p: 2, position: 'relative',
        }}>
          <IconButton size="small" onClick={() => setShowBanner(false)}
            sx={{ position: 'absolute', top: 8, right: 8, color: '#aaa', '&:hover': { color: '#555' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
          <Typography fontSize="0.85rem" color="#7a3a00" mb={1} pr={3}>
            <strong>Hur det fungerar:</strong> Du skriver ut fyra blad — ett per SWOT-kvadrant (S, W, O, T).
            Lägg ut ett blad per bord eller fördela dem mellan grupperna. Deltagarna skriver fritt i
            skrivytan. När sessionen är klar skannar du varje blad och AI extraherar alla bidrag direkt
            in i det digitala boardet.
          </Typography>
          <Box sx={{ bgcolor: '#fff3e0', border: '1px solid #ffcc80', borderRadius: 1, px: 2, py: 1 }}>
            <Typography fontSize="0.8rem" color="#7a3a00" fontWeight={700} mb={0.4}>
              Påminn deltagarna innan de börjar skriva:
            </Typography>
            <Typography fontSize="0.8rem" color="#8a4500" sx={{ lineHeight: 1.6 }}>
              ✏️ Skriv tydligt — gärna med VERSALER eller stor handstil<br />
              – Använd streck eller bindestreck för att hålla isär bidragen<br />
              🖊 Tjock penna eller tuschpenna är bäst för AI-tolkning<br />
              📄 Plats slut? Fortsätt på baksidan — baksidan behöver ingen QR-kod
            </Typography>
          </Box>
          {session.question && (
            <Box sx={{ mt: 1.5, p: 1.25, bgcolor: '#f0f7ff', border: '1px solid #90caf9', borderRadius: 1 }}>
              <Typography fontSize="0.78rem" color="#1565c0" fontWeight={700} mb={0.25}>Frågeställning:</Typography>
              <Typography fontSize="0.82rem" color="#1a237e">"{session.question}"</Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Utskriftsblad — ett per kvadrant (× grupp om grupper finns) */}
      <Box sx={{ p: { xs: 2, md: 3 }, '@media print': { p: 0 } }}>
        {/* Försättsblad */}
        <PrintCoverPage
          type="swot"
          title={session.title}
          subtitle={session.question || null}
          date={session.createdAt?.toDate ? session.createdAt.toDate() : null}
          facilitator={session.facilitator || session.createdByName || null}
          groups={groups}
          companyLogo={companyLogo}
          imageUrl={session.imageUrl || null}
          isA4={isA4}
        />
        {QUADS.flatMap((q) =>
          (groups || [null]).map((group) => ({ q, group }))
        ).map(({ q, group }) => (
          <Box
            key={`${q.key}-${group?.id || 'nogroup'}`}
            className="print-page"
            sx={{
              width: '100%',
              maxWidth: c.maxWidth,
              mx: 'auto',
              mb: 4,
              border: `1px solid ${q.color}44`,
              borderRadius: 2,
              overflow: 'hidden',
              position: 'relative',
              bgcolor: '#fff',
              color: '#000',
              display: 'flex',
              flexDirection: 'column',
              minHeight: c.minHeight,
              '@media print': {
                border: 'none', borderRadius: 0, maxWidth: '100%', mb: 0,
                boxShadow: 'none', height: isA4 ? '194mm' : '281mm', overflow: 'hidden',
              },
            }}
          >
            {/* Header */}
            <Box sx={{
              display: 'flex', alignItems: 'stretch',
              borderBottom: `4px solid ${q.color}`,
              flexShrink: 0,
            }}>
              {/* Färgad SWOT-identifierare */}
              <Box sx={{
                position: 'relative',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', px: isA4 ? 2 : 3,
                bgcolor: q.color,
                minWidth: isA4 ? 90 : 120,
                flexShrink: 0,
                gap: 0.75,
                overflow: 'hidden',
              }}>
                {/* Bokstav-watermark i bakgrunden */}
                <Typography sx={{
                  position: 'absolute',
                  fontSize: isA4 ? '5rem' : '7rem', fontWeight: 900,
                  color: 'rgba(255,255,255,0.1)',
                  lineHeight: 1, fontFamily: "'DM Sans', sans-serif",
                  userSelect: 'none', pointerEvents: 'none',
                  bottom: -8,
                }}>
                  {q.letter}
                </Typography>
                {/* Ikon */}
                <Box sx={{
                  width: isA4 ? 36 : 48, height: isA4 ? 36 : 48,
                  borderRadius: '10px',
                  bgcolor: 'rgba(255,255,255,0.2)',
                  border: '1.5px solid rgba(255,255,255,0.35)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  zIndex: 1,
                }}>
                  <q.Icon sx={{ fontSize: isA4 ? '1.3rem' : '1.7rem', color: '#fff' }} />
                </Box>
                {/* Label */}
                <Typography sx={{
                  fontSize: c.labelSize, fontWeight: 700, color: 'rgba(255,255,255,0.95)',
                  letterSpacing: 1, textTransform: 'uppercase', zIndex: 1,
                }}>
                  {q.label}
                </Typography>
              </Box>

              {/* Session-info + frågeställning */}
              <Box sx={{ flex: 1, px: isA4 ? 2 : 2.5, py: isA4 ? 1.25 : 2, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography fontSize="0.5rem" color="#888" fontWeight={700} letterSpacing={1} mb={0.25}>
                  SWOT-ANALYS
                </Typography>
                <Typography fontSize={c.titleSize} fontWeight={700} color="#1a1a1a" mb={0.35} noWrap>
                  {session.title}
                </Typography>
                {session.question && (
                  <Typography fontSize={isA4 ? '0.6rem' : '0.72rem'} color="#555" mb={0.25}
                    sx={{ borderLeft: `2px solid ${q.color}66`, pl: 0.75, fontStyle: 'italic' }}>
                    {session.question}
                  </Typography>
                )}
                {session.createdAt && (
                  <Typography fontSize={isA4 ? '0.48rem' : '0.58rem'} color="#aaa">
                    {new Date(session.createdAt?.toDate?.() || Date.now()).toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </Typography>
                )}
              </Box>

              {/* Grupp-chip (om grupper finns) */}
              {group && (
                <Box sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  px: isA4 ? 1.5 : 2, flexShrink: 0,
                }}>
                  <Box sx={{
                    px: 1.5, py: 0.75, borderRadius: 1.5,
                    bgcolor: (group.color || accent) + '18',
                    border: `2px solid ${group.color || accent}`,
                    textAlign: 'center', minWidth: isA4 ? 72 : 90,
                  }}>
                    <Typography fontSize={isA4 ? '0.44rem' : '0.54rem'} fontWeight={700} letterSpacing={1}
                      color={group.color || accent}>GRUPP</Typography>
                    <Typography fontSize={isA4 ? '0.7rem' : '0.9rem'} fontWeight={800}
                      color={group.color || accent}>{group.name}</Typography>
                  </Box>
                </Box>
              )}

            </Box>

            {/* Fråge-prompt */}
            <Box sx={{ px: isA4 ? 2.5 : 3, pt: isA4 ? 1.25 : 2, pb: 0.5, flexShrink: 0 }}>
              <Typography fontSize={c.promptSize} fontWeight={700} color="#1a1a1a" mb={0.3} sx={{ lineHeight: 1.3 }}>
                {q.prompt}
              </Typography>
              <Typography fontSize={c.hintSize} color="#666" sx={{ lineHeight: 1.4 }}>
                {q.hint}
              </Typography>
              {/* Exempelruta */}
              <Box sx={{
                display: 'flex', gap: isA4 ? 0.5 : 0.75, mt: 0.75, flexWrap: 'wrap',
              }}>
                <Typography fontSize={c.exampleFontSize} color="#999" fontWeight={700} sx={{ alignSelf: 'center' }}>
                  Exempel:
                </Typography>
                {q.examples.map((ex, i) => (
                  <Box key={i} sx={{ px: 0.75, py: 0.1, border: '0.5px solid #ccc', borderRadius: 0.5 }}>
                    <Typography fontSize={c.exampleFontSize} color="#999" fontStyle="italic">"{ex}"</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Skrivyta */}
            <Box sx={{
              mx: isA4 ? 2.5 : 3, mt: 0.75,
              border: `2px dashed ${q.color}44`,
              borderRadius: 1.5,
              flex: 1,
              display: 'flex', flexDirection: 'column',
              position: 'relative',
              overflow: 'hidden',
              '@media print': { flex: 1 },
            }}>
              {/* Tips-rad */}
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: isA4 ? 1.5 : 2,
                px: 1.5, py: 0.4,
                borderBottom: `1px dashed ${q.color}28`,
                flexShrink: 0, flexWrap: 'wrap',
              }}>
                <Typography fontSize={c.tipsFontSize} color="#999" fontStyle="italic" noWrap>Tips:</Typography>
                <Typography fontSize={c.tipsFontSize} color="#999" noWrap>✏ Skriv tydligt / VERSALER</Typography>
                <Typography fontSize={c.tipsFontSize} color="#999" noWrap>– Bindestreck mellan bidragen</Typography>
                <Typography fontSize={c.tipsFontSize} color="#999" noWrap>🖊 Tjock penna är bäst</Typography>
                <Typography fontSize={c.tipsFontSize} color="#999" noWrap>📄 Plats slut? Fortsätt på baksidan</Typography>
              </Box>

              {/* Vattenmärke: stor bokstav */}
              <Box sx={{
                position: 'absolute', bottom: -10, right: 10,
                fontSize: isA4 ? '7rem' : '10rem',
                fontWeight: 900,
                color: q.color + '08',
                lineHeight: 1,
                userSelect: 'none',
                fontFamily: "'DM Sans', sans-serif",
              }}>
                {q.letter}
              </Box>

              {/* Tom skrivyta */}
              <Box sx={{ flex: 1 }} />
            </Box>

            {/* Footer */}
            <Box className="print-footer" sx={{
              display: 'flex', alignItems: 'stretch',
              mx: isA4 ? 2.5 : 3, mt: 0.75, mb: 1,
              border: '1px solid #eee',
              borderRadius: 1.5,
              overflow: 'hidden',
              flexShrink: 0,
            }}>
              {/* Kolumn 1: Uppladdningsinstruktioner */}
              <Box sx={{ flex: 2, px: 1.25, py: 0.875, borderRight: '1px solid #eee' }}>
                <Typography fontSize={c.footerLabelSize} fontWeight={700} color="#888" mb={0.35} letterSpacing={0.5}>
                  SÅ HÄR LADDAR DU UPP SVAREN
                </Typography>
                {[
                  '① Skanna QR-koden med mobilkamerans kamera-app',
                  '② Fotografera hela bladet (text + QR-kod ska synas)',
                  '③ Skrivit på baksidan? Välj "Skanna baksidan" i appen',
                  '④ Granska AI-tolkade bidrag och tryck Spara',
                ].map((line, i) => (
                  <Typography key={i} fontSize={c.footerTextSize} color="#777" mb={0.15} sx={{ lineHeight: 1.3 }}>
                    {line}
                  </Typography>
                ))}
                <Typography fontSize={c.footerUrlSize} color="#999" fontFamily="monospace" mt={0.35}>
                  {scanUrl(q.key, group?.id).replace(origin, '')}
                </Typography>
              </Box>

              {/* Kolumn 2: Deltagare — namnrader */}
              <Box sx={{
                flex: 1.5, px: 1.25, py: 0.875, borderRight: '1px solid #eee',
                display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
              }}>
                <Typography fontSize={c.nameLabelSize} fontWeight={700} color="#888" mb={0.4} letterSpacing={0.5}>
                  DELTAGARE
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, flex: 1, justifyContent: 'space-around' }}>
                  {[0,1,2,3,4].map(n => (
                    <Box key={n} sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
                      <Typography fontSize={c.footerUrlSize} color="#bbb" sx={{ lineHeight: 1, pb: '1px', flexShrink: 0 }}>–</Typography>
                      <Box sx={{ flex: 1, borderBottom: '0.5px solid #bbb', minHeight: c.nameLineHeight }} />
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Kolumn 3: QR-kod */}
              <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                px: 1, py: 0.75, bgcolor: '#fff', flexShrink: 0,
              }}>
                <QRCodeSVG
                  value={scanUrl(q.key, group?.id)}
                  size={c.qrSize}
                  level="H"
                  {...(qrLogo ? { imageSettings: { src: qrLogo, height: c.qrLogoSize, width: c.qrLogoSize, excavate: true } } : {})}
                />
              </Box>
            </Box>

            {/* Företagslogga — nedre högra hörnet */}
            {companyLogo && (
              <Box
                component="img" src={companyLogo} alt=""
                sx={{
                  position: 'absolute',
                  bottom: isA4 ? 10 : 14,
                  right: isA4 ? 14 : 18,
                  height: c.companyLogoH,
                  maxWidth: c.companyLogoH * 4,
                  objectFit: 'contain',
                  opacity: 0.4,
                  pointerEvents: 'none',
                }}
              />
            )}
          </Box>
        ))}
      </Box>

      <style>{`
        @page { size: ${isA4 ? 'A4' : 'A3'} landscape !important; margin: 8mm !important; }
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; margin: 0; }
          .print-page {
            page-break-after: always !important;
            break-after: page !important;
            display: flex !important;
            flex-direction: column !important;
            height: ${isA4 ? '194mm' : '281mm'} !important;
            overflow: hidden !important;
          }
          .print-page:last-child {
            page-break-after: avoid !important;
            break-after: avoid !important;
          }
          .print-footer {
            break-inside: avoid !important;
            page-break-inside: avoid !important;
          }
        }
      `}</style>
    </>
  );
}
